import React from 'react';
import { Message } from '../types/chat.types';
import { useAppDispatch, useAppSelector } from '../shared/hooks/redux';
import { useVisualization } from '../shared/hooks/useVisualization';
import { useFeedbackModal } from '../shared/hooks/useFeedbackModal';
import { useAgentDomainSelector } from '../shared/hooks/useAgentDomainSelector';
import {
  createSessionThunk,
  sendMessageThunk,
  deleteSessionThunk,
} from '../store/thunks/sessionThunks';
import { ChatHeader } from '../shared/components/ChatHeader/ChatHeader';
import { ChatMessages } from '../shared/components/ChatMessages';
import { ChatInput } from '../shared/components/ChatInput/ChatInput';
import { HubWelcome } from '../shared/components/HubWelcome';
import { AgentWelcome } from '../shared/components/AgentWelcome';
import { FeedbackModal } from '../shared/components/FeedbackModal/FeedbackModal';
import { DataVisualizationModal } from '../shared/components/DataVisualizationModal';
import { appendDomainTag } from '../shared/components/AgentDomainSelector';
import { isVisualizationAgent } from '../config/visualizationAgents';
import { ChatArea as ChatArea__Root, ChatArea__Content, ChatArea__InputWrap } from './ChatArea.styles';

/** Stable empty array reference to avoid re-renders when no messages exist. */
const EMPTY_MESSAGES: Message[] = [];

interface ChatAreaProps {
  onToggleSidebar?: () => void;
}

export const ChatArea: React.FC<ChatAreaProps> = ({ onToggleSidebar }) => {
  const dispatch = useAppDispatch();
  const activeSessionId = useAppSelector((state) => state.session.activeSessionId);
  const messages = useAppSelector((state): Message[] => {
    const id = state.session.activeSessionId;
    if (!id) return EMPTY_MESSAGES;
    return state.session.messagesBySession[id] ?? EMPTY_MESSAGES;
  });
  const isLoading = useAppSelector((state) => state.session.isLoading);
  const activeAgent = useAppSelector((state) => state.agent.activeAgent);

  const { feedbackModal, handleFeedback, handleFeedbackClose, handleFeedbackSubmit } =
    useFeedbackModal();
  const {
    selectedDomain,
    setSelectedDomain,
    availableDomains,
    disabledDomains,
    domainSelectorEnabled,
    resetDomain,
  } = useAgentDomainSelector(activeAgent ?? null);
  const { vizLoading, vizModalData, handleVisualize, closeVizModal } =
    useVisualization(activeAgent ?? null);

  const handleSendMessage = async (message: string) => {
    if (!activeAgent) return;
    if (domainSelectorEnabled && !selectedDomain) return;

    try {
      if (!activeSessionId) {
        await dispatch(createSessionThunk()).unwrap();
      }

      dispatch(
        sendMessageThunk({
          message,
          apiMessage:
            domainSelectorEnabled && selectedDomain
              ? appendDomainTag(message, selectedDomain)
              : undefined,
        }),
      );
    } catch (err) {
      console.error('[ChatArea] Failed to send message:', err);
    }
  };

  const handleResetSession = async () => {
    try {
      if (activeSessionId) {
        await dispatch(deleteSessionThunk()).unwrap();
      }
      await dispatch(createSessionThunk()).unwrap();
    } catch (err) {
      console.error('[ChatArea] Failed to reset session:', err);
    }
    resetDomain();
  };

  return (
    <>
      <ChatArea__Root>
        <ChatHeader
          onResetSession={handleResetSession}
          onToggleSidebar={onToggleSidebar}
          selectedDomain={domainSelectorEnabled ? selectedDomain : undefined}
          availableDomains={domainSelectorEnabled ? availableDomains : undefined}
          disabledDomains={domainSelectorEnabled ? disabledDomains : undefined}
          onDomainSelect={domainSelectorEnabled ? setSelectedDomain : undefined}
          onVisualize={isVisualizationAgent(activeAgent?.name) ? handleVisualize : undefined}
          vizLoading={vizLoading}
        />

        <ChatArea__Content>
          {!activeAgent ? (
            <HubWelcome />
          ) : messages.length === 0 ? (
            <AgentWelcome onSuggestionClick={handleSendMessage} />
          ) : (
            <ChatMessages messages={messages} onFeedback={handleFeedback} />
          )}
        </ChatArea__Content>

        {activeAgent && (
          <ChatArea__InputWrap>
            <ChatInput
              onSend={handleSendMessage}
              disabled={isLoading}
              placeholder={
                domainSelectorEnabled && !selectedDomain
                  ? 'Select a domain area in the header before typing…'
                  : undefined
              }
            />
          </ChatArea__InputWrap>
        )}
      </ChatArea__Root>

      <FeedbackModal
        isOpen={!!feedbackModal}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
        initialReaction={feedbackModal?.reaction ?? 'thumbs-up'}
      />

      {vizModalData && (
        <DataVisualizationModal data={vizModalData} onClose={closeVizModal} />
      )}
    </>
  );
};
